import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  CircularProgress,
  Chip,
  ClickAwayListener,
  TablePagination,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
} from "@mui/material";
import {
  CalendarToday,
  Link as LinkIcon,
  TrendingUp,
  ContentCopy,
  WhatsApp,
  Share,
} from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import { RootState } from "../../../store/Index";
import { useSelector } from "react-redux";

import RequestPayouts from "./RequestPayouts";
import { BookingModal } from "./components/client-booking/BookingModal";
import TransactionsHistoryModal from "./components/TransactionHistoryModal";
import { useParams } from "react-router-dom";
import apiService, { BaseURL } from "../../../service/Api";
import ReferralNavbar from "./ReferralDashboardNavBar";
//import CreateBookingForm from "../../mms-landingpage/components/CreateBookingForm";
import TawkChat from "../TawkChat";
import ReferralQuickBookingForm from "./components/client-booking/ReferralQuickBookingForm";
import { toast } from "react-toastify";

// ── Types ────────────────────────────────────────────────────────────────────
interface Booking {
  id: number;
  refferal: {
    id: number;
    name: string;
    surname: string;
    email: string;
    status: string;
    commission_rate: number;
    phone_number: string;
  };
  booking: {
    id: number;
    title: string;
    status: string;
    total: string;
    currency: string;
    payment_status: string;
    source_of_booking: string;
    booking_type: string;
    service: string; // "Funeral" | "FuneralHome" | "Church" | "Conference" — matches StreamHeader.tsx's stream.booking.service
    created_at: string;
    deceased: {
      id: number;
      first_name: string;
      second_name: string; // added — needed to match the slug the backend generates in Stream.save()
      surname: string;
      date_of_birth: string;
      date_of_death: string;
    }[];
    package_item: {
      id: number;
      name: string;
      price: string;
    } | null;
    contact: {
      id: number;
      first_name: string;
      last_name: string;
      phone: string;
      email: string;
    } | null;
  };
  website_stream_link: string;
  commission_status: string;
  commission_rate_applied: string;
  commission_amount: string;
  commission_remaining_amount: string;
  commission_paid_amount: string;
  reward_paid: boolean;
  reward_paid_at: string | null;

  created_at: string;
}

interface ReferralProgramDetails {
  id: number;
  payment_detail: unknown | null;
  total_referrals: number;
  conversion_rate: number;
  referral_name: string | null;
  display_name: string | null;
  referral_surname: string | null;
  business_name: string | null;
  category: string;
  status: string;
  agreed_to_terms: boolean;
  business_address: string | null;
  commission_rate: number;
  special_request: string | null;
  internal_notes: string | null;
  created_at: string;
  referral_partner: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const TAB_FILTERS = ["all", "pending", "booked", "completed"];

const fallback = (val?: string | null) => val || "—";

const getClientName = (item: Booking): string => {
  const c = item.booking?.contact;
  return [c?.first_name, c?.last_name].filter(Boolean).join(" ") || "—";
};

const getClientPhone = (item: Booking): string =>
  item.booking?.contact?.phone ?? "";

const getInitials = (fullName: string) =>
  fullName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const getStatusStyles = (status?: string | null) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "confirmed")
    return { backgroundColor: "#E8F5E9", color: "#2E7D32", label: "confirmed" };
  if (normalized === "completed")
    return { backgroundColor: "#E3F2FD", color: "#1565C0", label: "Completed" };
  if (normalized === "cancelled")
    return { backgroundColor: "#FFEBEE", color: "#C62828", label: "Cancelled" };
  if (normalized === "enquiry")
    return { backgroundColor: "#FFF8E1", color: "#F57F17", label: "Enquiry" };
  if (normalized === "pending")
    return { backgroundColor: "#FFF3E0", color: "#E65100", label: "Pending" };
  if (normalized === "fulfilled")
    return { backgroundColor: "#E8F5E9", color: "#2E7D32", label: "Fulfilled" };
  return {
    backgroundColor: "#EEF2F6",
    color: "#55657D",
    label: status || "N/A",
  };
};

const getCommissionPaymentChip = (item: Booking) => {
  const total = parseFloat(item.commission_amount ?? "0");
  const paid = parseFloat(item.commission_paid_amount ?? "0");
  const remaining = parseFloat(item.commission_remaining_amount ?? "0");

  if (total === 0) {
    return { label: "—", bg: "transparent", color: "#888" };
  }
  if (remaining === 0) {
    return { label: "Paid", bg: "#E8F5E9", color: "#2E7D32" };
  }
  if (paid > 0) {
    return { label: "Partial", bg: "#FFF8E1", color: "#F59E0B" };
  }
  return { label: "Pending", bg: "#FFF3E0", color: "#E65100" };
};

// ── Component ────────────────────────────────────────────────────────────────
const ReferralDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletCurrency, setWalletCurrency] = useState("USD");
  const [walletHistoryOpen, setWalletHistoryOpen] = useState(false);

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [bookingsError, setBookingsError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const { id } = useParams<{ id: string }>();
  const [showLinkBox, setShowLinkBox] = useState(false);
  const shareUrl = `${window.location.origin}/referral/${id}/client-booking`;

  const [, setPartnerData] = useState<ReferralProgramDetails | null>(null);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success("Link copied!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
    });
  };

  const [navigatingBookingId] = useState<number | null>(null);

  const isStaging = BaseURL.includes("staging.muzukuru.com");

  const createSlug = (text: string) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const buildMemorialUrl = (item: Booking) => {
    const id = item.booking.id;
    const serviceType = item.booking?.service;

    let slugSource = "";
    let pageType = "";

  
    const normalisedServiceType = (serviceType || "")
      .replace(/[_\s-]/g, "")
      .toLowerCase();

    if (
      normalisedServiceType === "funeral" ||
      normalisedServiceType === "funeralhome"
    ) {
      const deceased = item.booking?.deceased?.[0];
      slugSource = [
        deceased?.first_name,
        deceased?.second_name,
        deceased?.surname,
      ]
        .filter(Boolean)
        .join(" ");
      pageType = "memorial-page";
    } else if (serviceType === "Church") {
      slugSource = item.booking?.title || "church-service";
      pageType = "church-page";
    } else if (serviceType === "Conference") {
      slugSource = item.booking?.title || "conference";
      pageType = "conference-page";
    } else {
      slugSource = "service";
      pageType = "service-page";
    }

    const domain = isStaging
      ? "https://staging.muzukuru.com"
      : "https://muzukuru.com";

    return `${domain}/${pageType}/${id}/${encodeURIComponent(createSlug(slugSource))}`;
  };

  const goToStreamPage = (item: Booking) => {
    const url = buildMemorialUrl(item);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleWhatsAppShare = () => {
    const message = `Hello, you can make your booking using this link:\n${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Booking Link",
          text: "Use this link to make your booking",
          url: shareUrl,
        });
      } catch (err) {
        console.error("Share cancelled", err);
      }
    } else {
      handleCopyLink();
    }
  };

  const fetchBookings = useCallback(async () => {
    if (!id) return;
    try {
      setLoadingBookings(true);
      setBookingsError("");
      const response = await apiService.get<{ results: Booking[] }>(
        `/api/refferal-program/refferal-bookings/?refferal=${id}`,
      );
      setBookings(response.data.results ?? []);
    } catch {
      setBookingsError("Failed to load referrals.");
    } finally {
      setLoadingBookings(false);
    }
  }, [id]);
  const fetchPartnerData = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiService.get<ReferralProgramDetails>(
        `/api/refferal-program/refferal-programs/${id}/`,
      );
      setPartnerData(res.data);
    } catch (err) {
      console.error("Failed to load referral program performance details", err);
    }
  }, [id]);

  const fetchWallet = useCallback(async () => {
    try {
      const res = await apiService.get<{ balance: string; currency: string }>(
        "/api/wallet/me/",
      );
      setWalletBalance(parseFloat(res.data.balance));
      setWalletCurrency(res.data.currency);
    } catch (err) {
      console.error("Failed to load wallet", err);
    }
  }, []);

  const refetchAll = useCallback(async () => {
    await Promise.all([fetchBookings(), fetchPartnerData(), fetchWallet()]);
  }, [fetchBookings, fetchPartnerData, fetchWallet]);

  useEffect(() => {
    fetchPartnerData();
  }, [fetchPartnerData]);

  useEffect(() => {
    const handleFocus = () => fetchBookings();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchBookings]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    fetchWallet();
    const interval = setInterval(fetchWallet, 30000);
    return () => clearInterval(interval);
  }, [fetchWallet]);

  const filteredBookings = bookings.filter((item) => {
    const activeFilter = TAB_FILTERS[tabValue];
    if (activeFilter === "all") return true;

    const status = (item.booking?.status ?? "").toLowerCase();

    if (activeFilter === "pending") {
      return status === "enquiry" || status === "pending";
    }
    if (activeFilter === "booked") {
      return (
        status === "upcoming" || status === "confirmed" || status === "booked"
      );
    }
    if (activeFilter === "completed") {
      return status === "fulfilled" || status === "completed";
    }

    return true;
  });

  const paginatedBookings = filteredBookings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: reduxTheme.primary_colors?.[800],
        px: { xs: 1, sm: 1.5, md: 2.5 },
        py: { xs: 2, md: 3 },
        overflowX: "hidden",
      }}
    >
      <ReferralNavbar />
      <TawkChat />

      {/* Header Section */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            color: reduxTheme.primary_colors?.[400],
            fontWeight: 700,
            mb: 1,
            mt: { xs: 2, md: 4 },
            fontSize: { xs: "19px", sm: "23px", md: "27px" },
            lineHeight: 1.25,
            px: 1,
          }}
        >
          Track your referrals. Earn with every booking.
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: "12.5px", sm: "14px", md: "15px" },
            px: 2,
            fontWeight: 400,
            mb: 3,
          }}
        >
          Your partnership with Muzukuru — helping families connect across
          distance.
        </Typography>
        <Box
          sx={{
            px: { xs: 1, sm: 0 },
          }}
        >
          {/* Action Buttons */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(180px, 230px))",
              },
              justifyContent: "center",
              gap: { xs: 1.25, sm: 2 },
              width: "100%",
              mb: 3,
            }}
          >
            <Button
              variant="contained"
              startIcon={<CalendarToday />}
              onClick={() => setBookingOpen(true)}
              sx={{
                background: `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[10]} 100%)`,
                borderRadius: "24px",
                px: { xs: 2, md: 4 },
                py: { xs: 1, md: 1.5 },
                textTransform: "none",
                height: { xs: "76px", sm: "110px", md: "125px" },
              }}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: { xs: "13px", sm: "14px" } }}
                >
                  Create Booking
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.8, fontSize: { xs: "11px", sm: "12px" } }}
                >
                  Help your client. Book now
                </Typography>
              </Box>
            </Button>

            <ClickAwayListener onClickAway={() => setShowLinkBox(false)}>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  width: "100%",
                  flexDirection: "column",
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => setShowLinkBox((prev) => !prev)}
                  sx={{
                    borderColor: "#ddd",
                    color: "#8B5A2B",
                    borderRadius: "24px",
                    px: { xs: 2, md: 4 },
                    py: { xs: 1, md: 1.5 },
                    textTransform: "none",
                    height: { xs: "76px", sm: "110px", md: "125px" },
                    bgcolor: "white",
                  }}
                >
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "13px", sm: "14px" },
                      }}
                    >
                      Share booking link
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "#888",
                        fontSize: { xs: "11px", sm: "12px" },
                      }}
                    >
                      Let client book on their own
                    </Typography>
                  </Box>
                </Button>

                {showLinkBox && (
                  <Paper
                    sx={{
                      position: "absolute",
                      top: "calc(100% + 10px)",
                      left: 0,
                      right: 0,
                      borderRadius: "16px",
                      overflow: "hidden",
                      zIndex: 20,
                      boxShadow: isMobile
                        ? "0 8px 24px rgba(0,0,0,0.15)"
                        : "none",
                      width: "100%",
                    }}
                  >
                    {/* Link preview */}
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #f1f1f1",
                        bgcolor: "#fafafa",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          wordBreak: "break-all",
                          fontSize: "11px",
                        }}
                      >
                        {shareUrl}
                      </Typography>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Button
                        startIcon={<ContentCopy />}
                        onClick={() => {
                          handleCopyLink();
                          setShowLinkBox(false);
                        }}
                        sx={{
                          justifyContent: "flex-start",
                          px: 2,
                          py: 1.5,
                          color: "#333",
                          textTransform: "none",
                          borderRadius: 0,
                          fontSize: { xs: "13px", sm: "14px" },
                        }}
                      >
                        Copy Link
                      </Button>

                      <Button
                        startIcon={<WhatsApp />}
                        onClick={() => {
                          handleWhatsAppShare();
                          setShowLinkBox(false);
                        }}
                        sx={{
                          justifyContent: "flex-start",
                          px: 2,
                          py: 1.5,
                          color: "#333",
                          textTransform: "none",
                          borderRadius: 0,
                          fontSize: { xs: "13px", sm: "14px" },
                        }}
                      >
                        Share via WhatsApp
                      </Button>

                      <Button
                        startIcon={<Share />}
                        onClick={() => {
                          handleNativeShare();
                          setShowLinkBox(false);
                        }}
                        sx={{
                          justifyContent: "flex-start",
                          px: 2,
                          py: 1.5,
                          color: "#333",
                          textTransform: "none",
                          borderRadius: 0,
                          fontSize: { xs: "13px", sm: "14px" },
                        }}
                      >
                        More sharing options
                      </Button>
                    </Box>
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>
          </Box>
          <Box
            sx={{
              transform: {
                xs: "scale(1)",
                sm: "scale(0.92)",
                md: "scale(0.85)",
              },
              transformOrigin: "top center",
              mb: { xs: 2, md: "-50px" },
              "& > div > div:first-of-type": {
                backgroundColor: "white",
              },
            }}
          >
            <ReferralQuickBookingForm onSuccess={refetchAll} />
          </Box>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "1.3fr 3.6fr",
          },
          gap: { xs: 2, md: 3 },
          maxWidth: 1400,
          mx: "auto",
          mt: { xs: 3, md: 6 },
          alignItems: "stretch",
          px: { xs: 1.5, sm: 3, md: 5 },
        }}
      >
        {/* ── Available Balance Card ──────────────────────────────────────── */}
        <Paper
          sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: "16px",
            textAlign: "left",
            minHeight: { md: 320 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "none",
            width: "100%",
          }}
        >
          <Typography
            variant="overline"
            sx={{ fontSize: { xs: "12px", md: "14px" }, fontWeight: 500 }}
          >
            AVAILABLE BALANCE
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: 40, sm: 56, md: 75 },
              my: 1,
              wordBreak: "break-word",
            }}
          >
            {walletCurrency === "USD" ? "$" : walletCurrency}
            {walletBalance.toFixed(2)}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 2,
            }}
          >
            <TrendingUp sx={{ color: "#4CAF50", fontSize: 16 }} />
            <Typography
              variant="body2"
              sx={{ fontSize: { xs: "12.5px", sm: "14px" } }}
            >
              $10 Total Earned
            </Typography>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setPayoutOpen(true)}
            sx={{
              background: `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[10]} 100%)`,
              borderRadius: "8px",
              minHeight: "40px",
              textTransform: "none",
              mb: 1.5,
              fontSize: { xs: "12px", sm: "14px" },
              py: { xs: 1, sm: 1.2 },
            }}
          >
            Request Payout
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setWalletHistoryOpen(true)}
            sx={{
              borderColor: reduxTheme.primary_colors?.[400],
              color: reduxTheme.primary_colors?.[400],
              borderRadius: "8px",
              textTransform: "none",
              minHeight: "40px",
              fontSize: { xs: "12px", sm: "14px" },
              py: { xs: 1, sm: 1.2 },
            }}
          >
            View Wallet History
          </Button>
        </Paper>

        {/* ── My Referrals Card ───────────────────────────────────────────── */}
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: "16px",
            minHeight: { md: 320 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "none",
            width: "100%",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: { xs: "16px", sm: "1.25rem" } }}
            >
              My Referrals
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#888", fontSize: { xs: "12px", sm: "14px" } }}
            >
              {loadingBookings ? "—" : `${bookings.length} total`}
            </Typography>
          </Box>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={(_, val) => {
              setTabValue(val);
              setPage(0);
            }}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={false}
            allowScrollButtonsMobile
            sx={{
              mb: 1,
              flexShrink: 0,
              minHeight: "36px",
              "& .MuiTabs-root": { minHeight: "36px" },
              "& .MuiTab-root": {
                textTransform: "none",
                minWidth: "auto",
                px: 2,
                py: 0.5,
                minHeight: "36px",
                fontSize: { xs: "12.5px", sm: "14px" },
              },
              "& .Mui-selected": { bgcolor: "#F5E6D3", borderRadius: "20px" },
            }}
            TabIndicatorProps={{ sx: { display: "none" } }}
          >
            <Tab label="All" />
            <Tab label="Pending" />
            <Tab label="Booked" />
            <Tab label="Completed" />
          </Tabs>

          {/* Content area: cards on mobile, table on sm+ */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {loadingBookings ? (
              <Box
                sx={{
                  display: "grid",
                  placeItems: "center",
                  height: "100%",
                  py: 4,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            ) : bookingsError ? (
              <Box
                sx={{
                  display: "grid",
                  placeItems: "center",
                  height: "100%",
                  color: "error.main",
                  py: 4,
                }}
              >
                {bookingsError}
              </Box>
            ) : filteredBookings.length === 0 ? (
              <Box
                sx={{
                  display: "grid",
                  placeItems: "center",
                  height: "100%",
                  color: "#888",
                  py: 4,
                }}
              >
                No referrals found.
              </Box>
            ) : isMobile ? (
              // ── Mobile card list ──────────────────────────────────────
              <Stack spacing={1.5} sx={{ overflowY: "auto" }}>
                {paginatedBookings.map((item) => {
                  const clientName = getClientName(item);
                  const phone = getClientPhone(item);
                  const service = fallback(item.booking?.booking_type);
                  const packageName = fallback(
                    item.booking?.package_item?.name,
                  );
                  const bookingStatus = getStatusStyles(item.booking?.status);
                  const total = parseFloat(item.commission_amount ?? "0");
                  const paid = parseFloat(item.commission_paid_amount ?? "0");
                  const remaining = parseFloat(
                    item.commission_remaining_amount ?? "0",
                  );
                  const paymentChip = getCommissionPaymentChip(item);

                  return (
                    <Paper
                      key={item.id}
                      onClick={() => goToStreamPage(item)}
                      variant="outlined"
                      sx={{
                        p: 1.75,
                        borderRadius: "12px",
                        borderColor: "#eef1f4",
                        cursor: "pointer",
                        opacity:
                          navigatingBookingId === item.booking.id ? 0.5 : 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          mb: 1,
                        }}
                      >
                        <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>
                          {getInitials(clientName)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#333",
                              fontSize: "13px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {clientName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#666", fontSize: "11.5px" }}
                          >
                            {phone || "—"} · #{item.booking?.id ?? "—"}
                          </Typography>
                        </Box>
                        <Chip
                          label={bookingStatus.label}
                          sx={{
                            backgroundColor: bookingStatus.backgroundColor,
                            color: bookingStatus.color,
                            borderRadius: "6px",
                            fontSize: "10px",
                            height: "20px",
                            fontWeight: 500,
                          }}
                        />
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          rowGap: 0.75,
                          columnGap: 1,
                          fontSize: "11.5px",
                        }}
                      >
                        <Typography sx={{ fontSize: "11.5px", color: "#888" }}>
                          Date
                        </Typography>

                        <Typography sx={{ fontSize: "11.5px", color: "#888" }}>
                          Service
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "11.5px",
                            color: "#4a5568",
                            textAlign: "right",
                          }}
                        >
                          {service}
                        </Typography>

                        <Typography sx={{ fontSize: "11.5px", color: "#888" }}>
                          Package
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "11.5px",
                            color: "#4a5568",
                            textAlign: "right",
                          }}
                        >
                          {packageName}
                        </Typography>

                        <Typography sx={{ fontSize: "11.5px", color: "#888" }}>
                          Total
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "11.5px",
                            color: "#4a5568",
                            textAlign: "right",
                          }}
                        >
                          {item.booking?.total
                            ? `$${parseFloat(item.booking.total).toFixed(2)}`
                            : "—"}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 0.75,
                        }}
                      >
                        <Typography sx={{ fontSize: "11px", color: "#888" }}>
                          Commission{" "}
                          <Box
                            component="span"
                            sx={{ color: "#4a5568", fontWeight: 500 }}
                          >
                            {total ? `$${total.toFixed(2)}` : "—"}
                          </Box>
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#2E7D32" }}>
                          Paid {total ? `$${paid.toFixed(2)}` : "—"}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#E65100" }}>
                          Owed {total ? `$${remaining.toFixed(2)}` : "—"}
                        </Typography>
                        {paymentChip.label !== "—" && (
                          <Chip
                            label={paymentChip.label}
                            sx={{
                              backgroundColor: paymentChip.bg,
                              color: paymentChip.color,
                              borderRadius: "999px",
                              fontSize: "10px",
                              height: "18px",
                            }}
                          />
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              // ── Desktop / tablet table ────────────────────────────────
              <TableContainer
                sx={{
                  maxHeight: "100%",
                  overflowX: "hidden",
                  overflowY: "auto",
                }}
              >
                <Table
                  stickyHeader
                  sx={{
                    width: "100%",
                    tableLayout: "fixed",
                    "& .MuiTableCell-root": {
                      px: { sm: 0.5, md: 1 },
                      py: { sm: 0.7, md: 1 },
                      fontSize: { sm: "11px", md: "12px" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: "7%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Booking ID
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "16%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Client
                      </TableCell>

                      <TableCell
                        sx={{
                          width: "9%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Service
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "10%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Package
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "10%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "10%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "8%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Commission
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "8%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Paid
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "8%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Remaining
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "10%",
                          color: "#627282",
                          fontWeight: 500,
                          bgcolor: "#f1f5f9",
                          fontSize: "12px",
                        }}
                      >
                        Commission Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedBookings.map((item) => {
                      const clientName = getClientName(item);
                      const phone = getClientPhone(item);
                      const service = fallback(item.booking?.booking_type);
                      const packageName = fallback(
                        item.booking?.package_item?.name,
                      );
                      const bookingStatus = getStatusStyles(
                        item.booking?.status,
                      );
                      const total = parseFloat(item.commission_amount ?? "0");
                      const paid = parseFloat(
                        item.commission_paid_amount ?? "0",
                      );
                      const remaining = parseFloat(
                        item.commission_remaining_amount ?? "0",
                      );
                      const paymentChip = getCommissionPaymentChip(item);

                      return (
                        <TableRow
                          key={item.id}
                          hover
                          onClick={() => goToStreamPage(item)}
                          sx={{
                            cursor: "pointer",
                            opacity:
                              navigatingBookingId === item.booking.id ? 0.5 : 1,
                            "& td": {
                              borderBottom: "1px solid #f1f5f9",
                              py: 1,
                            },
                          }}
                        >
                          <TableCell
                            sx={{ fontSize: "11px", color: "#4a5568" }}
                          >
                            {item.booking?.id ?? "—"}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: { sm: 26, md: 28 },
                                  height: { sm: 26, md: 28 },
                                  fontSize: { sm: 11, md: 12 },
                                }}
                              >
                                {getInitials(clientName)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    color: "#333",
                                    fontSize: "11px",
                                    lineHeight: 1.1,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {clientName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#666",
                                    display: "block",
                                    fontSize: "11px",
                                    lineHeight: 1.1,
                                  }}
                                >
                                  {phone}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#4a5568",
                                fontSize: "11px",
                                lineHeight: 1.2,
                              }}
                            >
                              {service}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#4a5568",
                                fontSize: "11px",
                                lineHeight: 1.2,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {packageName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={bookingStatus.label}
                              sx={{
                                backgroundColor: bookingStatus.backgroundColor,
                                color: bookingStatus.color,
                                borderRadius: "6px",
                                fontSize: { sm: "9px", md: "11px" },
                                height: { sm: "18px", md: "22px" },
                                fontWeight: 500,
                                px: 0.5,
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "11px", color: "#4a5568" }}
                          >
                            {item.booking?.total
                              ? `$${parseFloat(item.booking.total).toFixed(2)}`
                              : "—"}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "11px", color: "#4a5568" }}
                          >
                            {total ? `$${total.toFixed(2)}` : "—"}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "11px", color: "#2E7D32" }}
                          >
                            {total ? `$${paid.toFixed(2)}` : "—"}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "11px", color: "#E65100" }}
                          >
                            {total ? `$${remaining.toFixed(2)}` : "—"}
                          </TableCell>
                          <TableCell>
                            {paymentChip.label === "—" ? (
                              <Typography
                                variant="caption"
                                sx={{ color: "#888" }}
                              >
                                —
                              </Typography>
                            ) : (
                              <Chip
                                label={paymentChip.label}
                                sx={{
                                  backgroundColor: paymentChip.bg,
                                  color: paymentChip.color,
                                  borderRadius: "999px",
                                  fontSize: "0.7rem",
                                  height: "20px",
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
          {!loadingBookings &&
            !bookingsError &&
            filteredBookings.length > 0 && (
              <TablePagination
                component="div"
                count={filteredBookings.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{
                  flexShrink: 0,
                  "& .MuiTablePagination-toolbar": {
                    minHeight: "40px",
                    px: 0,
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      fontSize: "11px",
                    },
                  "& .MuiTablePagination-spacer": {
                    display: { xs: "none", sm: "block" },
                  },
                }}
              />
            )}
        </Paper>
      </Box>

      <RequestPayouts
        referralId={id ?? ""}
        open={payoutOpen}
        onClose={() => {
          setPayoutOpen(false);
          fetchWallet();
        }}
      />
      <TransactionsHistoryModal
        open={walletHistoryOpen}
        onClose={() => setWalletHistoryOpen(false)}
      />
      <BookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        onSuccess={refetchAll}
      />
    </Box>
  );
};

export default ReferralDashboard;
